import { Apollo, gql } from 'apollo-angular';
import { AfterViewInit, Component, ElementRef, Inject, LOCALE_ID, PLATFORM_ID, ViewChild } from '@angular/core';
import { GameLauncherService } from 'src/app/services/games/game-launcher.service';
import { BasePageComponentWithDialogs } from 'src/app/components/base-page/base-page.component';
import { distinctUntilChanged, switchMap, take, takeUntil } from 'rxjs/operators';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import { GameItem, GameResponse } from 'src/app/apollo/models/base-models';
import { SwiperComponent } from 'swiper/angular';
import { MinimizeSiteService } from 'src/app/services/utils/hide-footer.service';
import { LastPlayedService } from 'src/app/services/profile/last-played.service';
import { gameInfoTrx } from 'src/app/router-translation.labels';
import { DEFAULT_GAME_BACKGROUND, SetMainBackgroundService } from 'src/app/services/desktop/set-main-background.service';
import { TranslationConfig } from 'src/app/utils/translate-config';
import { OpenDesktopDrawerService } from 'src/app/services/utils/open-desktop-drawer.service';
import { ClonerService } from 'src/app/services/utils/clone-object.service';
import { environment } from 'src/environments/environment';
import { LicenceService } from 'src/app/services/utils/licence.service';
import { DetectDeviceService } from 'src/app/services/utils/detect-device.service';
import { MatDialog } from '@angular/material/dialog';
import { LoginStatusService } from 'src/app/services/auth/login/login-status.service';
import { combineLatest, of, timer } from 'rxjs';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { SwiperOptions } from "swiper";
import { ViewportScroller } from '@angular/common';
import { DialogClosePreviousUrl } from 'src/app/services/utils/dialog-close-previouse-url.service';
import { BonusQueueService } from 'src/app/services/wallet/bonus-queue.service';
import { BonusExcludedGameComponent } from './bonus_excluded-dialog/bonus_excluded-dialog.component';

const query = gql`query LaunchGameQuery($gameName:String!,$locale: Locale!){
      game(locales: [$locale, en],where: {systemName: $gameName}) {
        name
        gameid
        provider
        systemName
        tag
        thumbnail {
          url
        }
        seo {
          metaTitle
          metaDescription
          relCanonical
        }
        thumbnailDesktopSquare{
          url(transformation: {
            image: { resize: { width: 206, height: 206, fit: clip } }
            document: { output: { format: webp } }
          })
        }
        thumbnailDesktopRectangular{
          url
        }
        shortDescription {
          html
        }
        animation{
          url
          fileName
        }
        gameCategory{
          gameType
          name
        }  
      }
  }
  `

@Component({
  selector: 'app-game-launcher',
  templateUrl: './game-launcher.component.html',
  styleUrls: ['./game-launcher.component.scss']
})
export class DesktopGameLauncherComponent extends BasePageComponentWithDialogs implements AfterViewInit {

  constructor(
    private gameLaucherService: GameLauncherService,
    private apollo: Apollo,
    private route: ActivatedRoute,
    private router: Router,
    private sanitizer: DomSanitizer,
    public detectDesktopService: DetectDeviceService,
    public minimizeSiteService: MinimizeSiteService,
    public lastPlayedService: LastPlayedService,
    private setBackgroundService: SetMainBackgroundService,
    private openDrawerService: OpenDesktopDrawerService,
    protected translationConfig: TranslationConfig,
    private loginStatusService: LoginStatusService,
    public errorDialog: MatDialog,
    private cloneService: ClonerService,
    protected licenceService: LicenceService,
    private returnUrlService: DialogClosePreviousUrl,
    @Inject(LOCALE_ID) public locale: string,
    @Inject(PLATFORM_ID) private platformId,
    @Inject(DOCUMENT) private document: Document,
    private viewportScroller: ViewportScroller,
    private bonusQueueService: BonusQueueService,
  ) { super(errorDialog, detectDesktopService) }

  safeUrl: SafeResourceUrl;
  gameItem: GameItem;
  games: GameItem[];
  gameId: string;
  isLoggedIn = false;
  provider: string;
  logged: boolean = false;
  enableTournamentsDisplay = environment.enableTournamentsDisplay;
  isReadMore = true;
  height: any = '220px';
  showMoreTrx = $localize`:@@button-show-more:Read more`;
  showLessTrx = $localize`:@@button-show-less:Read less`;
  bonusExcludedText=$localize`:@@bonus-excluded:Bonus Excluded`;
  GREEN_JADE_ARCADE_PRIVIDER = 'greenjade-arcade';
  @ViewChild('gamewindow') gamewindow: ElementRef;
  backUrl = "/";
  isBonusExcludedGame: boolean = false;

  readonly gameUrl = '/' + this.translationConfig.getTranslation(gameInfoTrx) + '/'
  is404Page: boolean = false;

  public config: SwiperOptions = {
    direction: 'horizontal',
    slideToClickedSlide: false,
    mousewheel: true,
    scrollbar: false,
    navigation: false,
    keyboard: true,
    freeMode: true,
    slidesPerView: 8,
    centerInsufficientSlides: false,
    centeredSlides: false,
    loop: false,
    simulateTouch: true,
    roundLengths: true,
    spaceBetween: 10
  };
  @ViewChild('swiper', { static: false }) swiper?: SwiperComponent;
  next() {
    this.swiper.swiperRef.slideNext();
    return false;
  }
  prev() {
    this.swiper.swiperRef.slidePrev();
    return false;
  }
  async onGameSelect(game: GameItem) {
    await this.router.navigate([this.translationConfig.getTranslation(gameInfoTrx), game.systemName]);
  }

  openBonusExcludedDialog(): void {
    this.errorDialog.open(BonusExcludedGameComponent, {
      width: '382px', // Customize size
      disableClose: true, // Prevent closing by clicking outside
      panelClass: ["bonusexclude-dialog"]
    });
  }

  triggerBonusExclude():void {
    this.bonusQueueService.getActiveBonus().pipe(take(1),takeUntil(this.unsubscribe)).subscribe(bonus => {
      this.isBonusExcludedGame = bonus?.excludedGames?.includes(this.gameItem?.gameid)
      if(this.isBonusExcludedGame){
        this.openBonusExcludedDialog(); // Open dialog when component initializes
      }
    })
  }

  ngOnInit(): void {

    this.returnUrlService.getUrl().pipe(take(1)).subscribe(path => {
      if (path) {
        this.backUrl = path.join('/');
      } else {
        this.backUrl = "/";
      }
    });

    this.setBackgroundService.setBackGroundImage(DEFAULT_GAME_BACKGROUND);
    this.openDrawerService.close();
    this.loginStatusService.getIfUserLogged().pipe(take(1)).subscribe((isLogged) => {
      this.isLoggedIn = true;
    });

    this.loginStatusService.getLoginStatus(false).pipe(
      take(1),
      switchMap((status) => {
        return this.route.paramMap.pipe(
          switchMap((params: ParamMap) => {
            return this.apollo
              .watchQuery<GameResponse>({
                query: query,
                variables: {
                  gameName: params.get('game'),
                  locale: this.locale
                }
              })
              .valueChanges
          },
          ),
          distinctUntilChanged((prev, curr) => prev.data.game.gameid === curr.data.game.gameid),
          switchMap((resp) => {
            const liveCasinoGame = resp.data.game.gameCategory.some((data)=>{
              return data.gameType == "live_casino";
            })
            
            if (!!resp.data.game) {
              this.gameItem = this.cloneService.deepClone(resp.data.game);
              if (resp?.data?.game?.shortDescription) {
                this.gameItem.shortDescription.safe = this.sanitizer.bypassSecurityTrustHtml(resp.data.game.shortDescription.html);
              };
              this.gameId = this.gameItem.gameid;
              if (resp.data.game.name) this.titleService.setTitle(resp.data.game.name);
              const pageJson = resp.data.game;
              const slotName = pageJson.name;
              const title = !!resp.data.game.seo?.metaTitle ? resp.data.game.seo.metaTitle : liveCasinoGame ? $localize`:@@title-livecasino-gamepage-meta:Play ${slotName} Live Casino Game at Bison Casino` : $localize`:@@title-game-page-meta:${slotName} slot game - Play the ${slotName} Demo Online`;
              const casinoName = this.licenceService.getKey("name");
              const description = !!resp.data.game.seo?.metaDescription
                ? resp.data.game.seo.metaDescription
                : liveCasinoGame ? $localize`:@@description-livecasino-gamepage-meta:Experience the excitement of ${slotName} with real dealers. Play ${slotName} live at ${casinoName} and enjoy a realistic casino experience!` : $localize`:@@description-game-page-meta:Popular slot ${slotName} already available at ${casinoName}. Play ${slotName} slot demo for free, register today to get your welcome bonus.`;
              this.setSEOTags(title, description);

              this.lastPlayedService.addLastPlayed(this.gameId)

            }
            else {
              this.is404Page = true;
            }
            return of(resp)
          }),
          switchMap((resp) => {
            return combineLatest({
              loginStatus: isPlatformBrowser(this.platformId) ? this.loginStatusService.getLoginStatus(false).pipe(take(1)) : of(false),
              cmsResp: of(resp),
            })
          }),
          switchMap((resp) => {
            if (!!resp.loginStatus) {
              return this.gameLaucherService.requestStartGame(this.gameId, false).pipe(take(1))
            }
            else { return of(null) }
          })
        )
      })).subscribe({
        next: (resp) => {
          if (!!resp) {
            this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(resp.config.game_url);
            this.triggerBonusExclude()
          }
        },
        error: (error) => {
          if (error.status == 403) {
            this.openErrorDialog(error.error?.error_message);
            // this.router.navigate([loginTrx], { queryParams: { redirectTo: this.router.url } });

          }
        }
      });



    this.lastPlayedService.getLastPlayed().pipe(takeUntil(this.unsubscribe)).subscribe(
      resp => {
        this.games = this.cloneService.deepClone(resp);
        this.games.forEach((item => {
          item.gameid = item.name.split(' ').join('_');
        }))
      }
    );

    this.loginStatusService.getLoginPartialStatus().pipe(takeUntil(this.unsubscribe)).subscribe({
      next: (res) => {
        this.logged = res;
      },
      error: () => {

      }
    })

  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      timer(1000).subscribe(() => {
        // this.gamewindow.nativeElement.scrollIntoView({ behavior: "smooth" })
        const el = this.document.querySelector('#breadcrumbs-desktop');
        el.scrollIntoView({ behavior: "smooth" });

      })
    }
  }



  scroll(target: HTMLElement) {
    target.scrollIntoView();
  }

  toggleData() {
    this.isReadMore = !this.isReadMore;
    if (!this.isReadMore) {
      this.height = '100%';
    } else {
      this.height = '220px';
    }
  }

  ngOnDestroy() {
    this.setBackgroundService.setBackGroundImage(null);
    // this.loginStatusService.udpateUserState('activeBonusWarningDisplayed',false);
    super.ngOnDestroy();
  }
}
